import { type ReactNode } from 'react'

import { ConversationErrorStubStyled } from './styles/ConversationStreamStyled'
import { ConfirmationOptions } from '../../../../components/ConfirmationOptions'
import { Message } from '../../../../components/Message'
import {
  type ConversationChoiceOption,
  type ConversationComponent,
  type ConversationTurnItem,
} from '../../../../value-objects/ConversationTurnItem'
import {
  ConversationComponentTypes,
  ConversationItemKinds,
} from '../../../../value-objects/enums'

interface RenderConversationItemOptions {
  isStreaming: boolean
  onChoiceSelect: (option: ConversationChoiceOption) => void
}

const isRenderableMessageContent = (content: string): boolean => {
  const trimmed = content.trim()
  return trimmed !== '' && trimmed !== '""'
}

function renderConversationComponent(
  component: ConversationComponent,
  key: string,
  disabled: boolean,
  onChoiceSelect: (option: ConversationChoiceOption) => void
): ReactNode {
  switch (component.type) {
    case ConversationComponentTypes.MESSAGE:
      if (!isRenderableMessageContent(component.content)) return null
      return (
        <Message
          key={key}
          sender={component.sender}
          content={component.content}
        />
      )
    case ConversationComponentTypes.CONFIRMATION_OPTIONS:
      return (
        <ConfirmationOptions
          key={key}
          options={component.options}
          disabled={disabled}
          onSelect={onChoiceSelect}
        />
      )
    default:
      return null
  }
}

export const renderConversationItem = (
  item: ConversationTurnItem,
  { isStreaming, onChoiceSelect }: RenderConversationItemOptions
): ReactNode => {
  switch (item.kind) {
    case ConversationItemKinds.MESSAGE:
      return <Message sender={item.sender} content={item.content} />
    case ConversationItemKinds.ENVELOPE:
      return item.components.map((component, index) =>
        renderConversationComponent(
          component,
          `${item.envelopeId}_${component.type}_${index}`,
          item.isAnswered || isStreaming,
          onChoiceSelect
        )
      )
    case ConversationItemKinds.ERROR:
      return (
        <ConversationErrorStubStyled role='alert'>
          {item.message}
        </ConversationErrorStubStyled>
      )
    default:
      return null
  }
}
